import { lazy } from 'react'
import { createBrowserRouter, Navigate } from 'react-router-dom'

// 懒加载页面组件
const LoginPage = lazy(() => import('@/pages/LoginPage'))
const ForgotPasswordPage = lazy(() => import('@/pages/ForgotPasswordPage'))
const DashboardPage = lazy(() => import('@/pages/DashboardPage'))
const HomePage = lazy(() => import('@/pages/HomePage'))
const CategoryPage = lazy(() => import('@/pages/CategoryPage'))
const CategoriesPage = lazy(() => import('@/pages/CategoriesPage'))
const MySpacePage = lazy(() => import('@/pages/MySpacePage'))
const FreeChatPage = lazy(() => import('@/pages/FreeChatPage'))
const AssistantDetailPage = lazy(() => import('@/pages/AssistantDetailPage'))
const ChatPage = lazy(() => import('@/pages/ChatPage'))
const ProfilePage = lazy(() => import('@/pages/ProfilePage'))
const CreatePage = lazy(() => import('@/pages/CreatePage'))
const NotFoundPage = lazy(() => import('@/pages/NotFoundPage'))

// V3.1 新增页面
const ScenarioInputPage = lazy(() => import('@/pages/ScenarioInputPage.tsx'))
const QuickInputPage = lazy(() => import('@/pages/QuickInputPage/QuickInputPage'))
const GuidedModePage = lazy(() => import('@/pages/GuidedModePage/GuidedModePage'))
const ResultPage = lazy(() => import('@/pages/ResultPage/ResultPage'))
const HistoryPage = lazy(() => import('@/pages/HistoryPage.tsx'))

// 睡前小故事专用页（健康生活分类卡直达）
const BedtimeStoryPage = lazy(() => import('@/pages/BedtimeStoryPage'))

// 定制页面
const CustomAgentPage = lazy(() => import('@/pages/CustomAgentPage'))
const AieCommunityPage = lazy(() => import('@/pages/AieCommunityPage'))
const AdminPage = lazy(() => import('@/pages/AdminPage'))
const KgCenterPage = lazy(() => import('@/pages/KgCenterPage'))
const SubgraphGraphPage = lazy(() => import('@/pages/SubgraphGraphPage'))
const KgSemanticGraphPage = lazy(() => import('@/pages/KgSemanticGraphPage'))
const KgDemoPage = lazy(() => import('@/pages/KgDemoPage'))
const MyGraphPage = lazy(() => import('@/pages/MyGraphPage'))
const StarMapPage = lazy(() => import('@/pages/StarMapPage'))

// 引导页
const OnboardingPage = lazy(() => import('@/pages/OnboardingPage'))
const MinorStatusPage = lazy(() => import('@/pages/MinorStatusPage'))

// 家庭版新增页面
const RegisterPage = lazy(() => import('@/pages/RegisterPage'))
const OAuthWechatLanding = lazy(() => import('@/pages/OAuthWechatLanding'))
const OAuthWechatAssociate = lazy(() => import('@/pages/OAuthWechatAssociate'))
const OAuthWechatPayReturn = lazy(() => import('@/pages/OAuthWechatPayReturn'))
const GroupManagePage = lazy(() => import('@/pages/GroupManagePage'))
const GroupDetailPage = lazy(() => import('@/pages/GroupDetailPage'))

// 我的小伙伴
const MyBuddiesPage = lazy(() => import('@/pages/MyBuddiesPage'))
const AgentChatPage = lazy(() => import('@/pages/AgentChatPage'))
// Stage 1：小伙伴对话改用原生 /manage iframe 渲染（FRONTEND-RENDERING-PLAN.md）；
// AgentChatPage（自研渲染）保留作 fallback，未删除
const AgentChatNativePage = lazy(() => import('@/pages/AgentChatNativePage'))

// 阶段0 demo：iframe 嵌原生 /manage（FRONTEND-RENDERING-PLAN.md）
const EmbedDemo = lazy(() => import('@/pages/EmbedDemo'))

// 法律合规页面
const TermsPage = lazy(() => import('@/pages/TermsPage').then(m => ({ default: m.default })))
const PrivacyPage = lazy(() => import('@/pages/TermsPage').then(m => ({ default: m.PrivacyPage })))

// 常见问题
const FaqPage = lazy(() => import('@/pages/FaqPage'))

// 路由配置
export const router = createBrowserRouter([
  {
    path: '/embed-demo',
    element: <EmbedDemo />,
  },
  {
    path: '/login',
    element: <LoginPage />,
  },
  {
    path: '/forgot-password',
    element: <ForgotPasswordPage />,
  },
  {
    path: '/kg-demo',
    element: <KgDemoPage />,
  },
  {
    path: '/my-graph',
    element: <MyGraphPage />,
  },
  {
    path: '/star-map',
    element: <StarMapPage />,
  },
  {
    path: '/register',
    element: <RegisterPage />,
  },
  {
    path: '/oauth/wechat/landing',
    element: <OAuthWechatLanding />,
  },
  {
    path: '/oauth/wechat/pay-return',
    element: <OAuthWechatPayReturn />,
  },
  {
    path: '/oauth/wechat/associate',
    element: <OAuthWechatAssociate />,
  },
  {
    path: '/dashboard',
    element: <DashboardPage />,
  },
  {
    path: '/',
    element: <HomePage />,
  },
  {
    path: '/categories',
    element: <CategoriesPage />,
  },
  {
    path: '/my-space',
    element: <MySpacePage />,
  },
  {
    path: '/chat/free',
    element: <FreeChatPage />,
  },
  {
    path: '/category/:id',
    element: <CategoryPage />,
  },
  {
    path: '/assistant/:id',
    element: <AssistantDetailPage />,
  },
  {
    path: '/chat',
    element: <ChatPage />,
  },
  {
    path: '/chat/:id',
    element: <ChatPage />,
  },
  {
    path: '/profile',
    element: <ProfilePage />,
  },
  {
    path: '/onboarding',
    element: <OnboardingPage />,
  },
  {
    path: '/minor',
    element: <MinorStatusPage />,
  },
  {
    path: '/create',
    element: <CreatePage />,
  },
  // V3.1 新增路由
  {
    path: '/scenario/:scenarioType',
    element: <ScenarioInputPage />,
  },
  {
    path: '/scenario/:scenarioType/quick',
    element: <QuickInputPage />,
  },
  {
    path: '/scenario/:scenarioType/guided',
    element: <GuidedModePage />,
  },
  {
    path: '/scenario/:scenarioType/result',
    element: <ResultPage />,
  },
  {
    path: '/bedtime-story',
    element: <BedtimeStoryPage />,
  },
  {
    path: '/history',
    element: <HistoryPage />,
  },
  {
    path: '/customize/:scenarioType',
    element: <CustomAgentPage />,
  },
  {
    path: '/community',
    element: <AieCommunityPage />,
  },
  {
    path: '/admin',
    element: <AdminPage />,
  },
  {
    path: '/admin/kg-graph',
    element: <Navigate to="/admin/knowledge-graphs?tab=structured" replace />,
  },
  {
    path: '/admin/knowledge-graphs',
    element: <KgCenterPage />,
  },
  {
    path: '/admin/kg-review',
    element: <Navigate to="/admin/knowledge-graphs?tab=promote" replace />,
  },
  {
    path: '/admin/knowledge-graphs/:id',
    element: <SubgraphGraphPage />,
  },
  {
    path: '/admin/knowledge-graphs/:id/semantic',
    element: <KgSemanticGraphPage />,
  },
  // 家庭管理已合并进统一「我的群组」——旧链接重定向，避免书签失效
  {
    path: '/family/setup',
    element: <Navigate to="/group/manage" replace />,
  },
  {
    path: '/family/manage',
    element: <Navigate to="/group/manage" replace />,
  },
  {
    path: '/family/debug',
    element: <Navigate to="/group/manage" replace />,
  },
  {
    path: '/family/reset',
    element: <Navigate to="/" replace />,
  },
  // 班级管理已合并进统一「我的群组」——旧链接重定向，避免书签失效
  {
    path: '/class/manage',
    element: <Navigate to="/group/manage" replace />,
  },
  {
    path: '/class/manage/:familyId',
    element: <Navigate to="/group/manage" replace />,
  },
  {
    path: '/group/manage',
    element: <GroupManagePage />,
  },
  {
    path: '/group/manage/:id',
    element: <GroupDetailPage />,
  },
  {
    path: '/my-buddies',
    element: <MyBuddiesPage />,
  },
    {
    path: '/my-buddies/:agentId',
    element: <AgentChatPage />,
    },
    // native iframe 版（/manage 嵌入）——无头验证可渲染，但真实浏览器卡 auth/cookie，
    // 暂留作 /my-buddies-native 备用；主线回到自研 AgentChatPage（登录态可靠 + /mnt/data 中间件改写出图）
    {
    path: '/my-buddies-native/:agentId',
    element: <AgentChatNativePage />,
    },
  {
    path: '/terms',
    element: <TermsPage />,
  },
  {
    path: '/privacy',
    element: <PrivacyPage />,
  },
  {
    path: '/faq',
    element: <FaqPage />,
  },
  {
    path: '/404',
    element: <NotFoundPage />,
  },
  {
    path: '*',
    element: <Navigate to="/404" replace />,
  },
])
